import * as PIXI from 'pixi.js';

const TRACK_COLOR = '#8b9cb4';
const KNOB_BORDER = '#9aabc2';
const KNOB_BORDER_ACTIVE = '#d7e4f6';
const LABEL_GAP = 8;

const KNOB_FILL = new PIXI.FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    colorStops: [
        { offset: 0, color: '#54647e' },
        { offset: 1, color: '#323e54' },
    ],
});

function decimalPlaces(value) {
    const text = String(value);
    const index = text.indexOf('.');
    if (index === -1 || text.includes('e')) return 0;
    return text.length - index - 1;
}

function snapToStep(value, min, max, step, places) {
    const clamped = Math.min(max, Math.max(min, value));
    const index = Math.round((clamped - min) / step);
    let snapped = Number((min + index * step).toFixed(places));
    if (snapped > max) snapped = Number((min + (index - 1) * step).toFixed(places));
    if (snapped < min) snapped = min;
    if (Math.abs(max - clamped) <= Math.abs(snapped - clamped)) return max;
    return snapped;
}

export default class RangeSlider extends PIXI.Container {
    constructor({
        min = 0,
        max = 100,
        step = 1,
        low,
        high,
        width = 640,
        knobRadius = 32,
        onChange = null,
    } = {}) {
        super();

        this.label = 'rangeSlider';
        this.min = Number.isFinite(min) ? min : 0;
        this.step = step > 0 ? step : 1;
        this.max = Number.isFinite(max) && max > this.min ? max : this.min + this.step;
        this.places = Math.max(decimalPlaces(this.min), decimalPlaces(this.max), decimalPlaces(this.step));
        this.knobRadius = knobRadius;
        this.borderWidth = Math.max(3, Math.round(knobRadius * 0.16));
        this.trackHeight = Math.max(6, Math.round(knobRadius * 0.25));
        this.fontSize = Math.round(knobRadius * 1.05);
        this.onChange = onChange;
        this.sidePad = knobRadius;
        this.barWidth = Math.max(width, this.sidePad * 2 + 1);

        const initialLow = low ?? this.min;
        const initialHigh = high ?? this.max;
        this.low = this.snap(Math.min(initialLow, initialHigh));
        this.high = this.snap(Math.max(initialLow, initialHigh));
        this.keepApart();

        this.dragging = null;
        this.pendingStack = false;

        this.track = new PIXI.Graphics();
        this.addChild(this.track);
        this.drawTrack();

        this.lowKnob = this.createKnob();
        this.highKnob = this.createKnob();
        this.fitSidePad(width);
        this.positionKnobs();

        this.barHeight = this.knobRadius * 2 + LABEL_GAP + this.lowKnob.labelText.height;
        this.layout = { width: this.barWidth, height: this.barHeight };
        this.hitArea = new PIXI.Rectangle(0, 0, this.barWidth, this.barHeight);
        this.eventMode = 'static';
        this.cursor = 'ew-resize';

        this.on('pointerdown', this.onPointerDown, this);
        this.on('pointerup', this.endDrag, this);
        this.on('pointerupoutside', this.endDrag, this);
        this.on('pointercancel', this.endDrag, this);
        this.on('globalpointermove', this.onPointerMove, this);
    }

    labelStyle() {
        return {
            fontFamily: 'Open Sans',
            fontSize: this.fontSize,
            fill: '#ffffff',
            fontWeight: 'bold',
        };
    }

    fitSidePad(width) {
        this.lowKnob.labelText.text = this.formatValue(this.min);
        this.highKnob.labelText.text = this.formatValue(this.max);
        const labelPad = Math.max(this.lowKnob.labelText.width, this.highKnob.labelText.width) / 2;
        if (labelPad <= this.sidePad) return;
        this.sidePad = labelPad;
        this.barWidth = Math.max(width, this.sidePad * 2 + 1);
        this.drawTrack();
    }

    createKnob() {
        const knob = new PIXI.Container();
        knob.graphics = new PIXI.Graphics();
        knob.addChild(knob.graphics);

        knob.labelText = new PIXI.Text({ text: '', style: this.labelStyle() });
        knob.labelText.anchor.set(0.5, 0);
        knob.labelText.y = this.knobRadius + LABEL_GAP;
        knob.addChild(knob.labelText);

        this.drawKnob(knob.graphics, false);
        this.addChild(knob);
        return knob;
    }

    drawTrack() {
        const x = this.sidePad;
        const y = this.knobRadius - this.trackHeight / 2;
        const width = this.barWidth - this.sidePad * 2;
        this.track.clear();
        this.track.roundRect(x, y, width, this.trackHeight, this.trackHeight / 2);
        this.track.fill({ color: TRACK_COLOR });
    }

    drawKnob(graphics, active) {
        const radius = this.knobRadius;
        graphics.clear();
        graphics.circle(0, 0, radius);
        graphics.fill({ color: active ? KNOB_BORDER_ACTIVE : KNOB_BORDER });
        graphics.circle(0, 0, radius - this.borderWidth);
        graphics.fill(KNOB_FILL);
    }

    snap(value) {
        return snapToStep(value, this.min, this.max, this.step, this.places);
    }

    stepFrom(value, direction) {
        const base = this.snap(value);
        const target = Number((base + direction * this.step).toFixed(this.places));
        const stepped = this.snap(target);
        if (direction > 0) return stepped > base ? stepped : (this.max > base ? this.max : base);
        return stepped < base ? stepped : (this.min < base ? this.min : base);
    }

    keepApart() {
        if (this.low < this.high) return;
        const above = this.stepFrom(this.low, 1);
        if (above > this.low) {
            this.high = above;
            return;
        }
        this.low = this.stepFrom(this.high, -1);
    }

    formatValue(value) {
        return this.places === 0 ? String(Math.round(value)) : value.toFixed(this.places);
    }

    valueToX(value) {
        const span = this.barWidth - this.sidePad * 2;
        const t = (value - this.min) / (this.max - this.min);
        return this.sidePad + t * span;
    }

    xToValue(x) {
        const span = this.barWidth - this.sidePad * 2;
        const t = (x - this.sidePad) / span;
        return this.snap(this.min + t * (this.max - this.min));
    }

    positionKnobs() {
        const lowX = this.valueToX(this.low);
        const highX = this.valueToX(this.high);
        this.lowKnob.position.set(lowX, this.knobRadius);
        this.highKnob.position.set(highX, this.knobRadius);
        this.lowKnob.labelText.text = this.formatValue(this.low);
        this.highKnob.labelText.text = this.formatValue(this.high);

        const gap = 8;
        const overlap = (this.lowKnob.labelText.width + this.highKnob.labelText.width) / 2 + gap - (highX - lowX);
        let lowShift = 0;
        let highShift = 0;
        if (overlap > 0) {
            lowShift = -overlap / 2;
            highShift = overlap / 2;
            const lowLeft = lowX + lowShift - this.lowKnob.labelText.width / 2;
            if (lowLeft < 0) {
                lowShift -= lowLeft;
                highShift -= lowLeft;
            }
            const highRight = highX + highShift + this.highKnob.labelText.width / 2;
            const overflow = highRight - this.barWidth;
            if (overflow > 0) {
                lowShift -= overflow;
                highShift -= overflow;
            }
        }
        this.lowKnob.labelText.x = lowShift;
        this.highKnob.labelText.x = highShift;
    }

    setValues(low, high) {
        this.low = this.snap(Math.min(low, high));
        this.high = this.snap(Math.max(low, high));
        this.keepApart();
        this.positionKnobs();
    }

    localX(event) {
        return this.toLocal(event.global).x;
    }

    onPointerDown(event) {
        const x = this.localX(event);
        const lowX = this.valueToX(this.low);
        const highX = this.valueToX(this.high);
        this.dragOrigin = x;
        this.pendingStack = Math.abs(highX - lowX) < 1;
        const closerToHigh = Math.abs(x - highX) < Math.abs(x - lowX);
        this.dragging = !this.pendingStack && closerToHigh ? 'high' : 'low';
        const knobX = this.dragging === 'high' ? highX : lowX;
        this.grabOffset = Math.abs(x - knobX) <= this.knobRadius ? x - knobX : 0;
        if (!this.pendingStack) this.applyPointer(x);
        this.raiseActiveKnob();
        event.stopPropagation();
    }

    onPointerMove(event) {
        if (!this.dragging) return;
        const x = this.localX(event);
        if (this.pendingStack) {
            if (Math.abs(x - this.dragOrigin) < 2) return;
            this.dragging = x < this.dragOrigin ? 'low' : 'high';
            this.pendingStack = false;
            this.raiseActiveKnob();
        }
        this.applyPointer(x);
        event.stopPropagation();
    }

    applyPointer(x) {
        const value = this.xToValue(x - this.grabOffset);
        if (this.dragging === 'low') {
            const next = Math.min(value, this.stepFrom(this.high, -1));
            if (next >= this.high || next === this.low) return;
            this.low = next;
        } else {
            const next = Math.max(value, this.stepFrom(this.low, 1));
            if (next <= this.low || next === this.high) return;
            this.high = next;
        }
        this.positionKnobs();
        this.onChange?.({ low: this.low, high: this.high });
    }

    raiseActiveKnob() {
        const active = this.dragging === 'high' ? this.highKnob : this.lowKnob;
        this.addChild(active);
        this.drawKnob(this.lowKnob.graphics, this.dragging === 'low');
        this.drawKnob(this.highKnob.graphics, this.dragging === 'high');
    }

    endDrag() {
        if (!this.dragging) return;
        this.dragging = null;
        this.pendingStack = false;
        this.drawKnob(this.lowKnob.graphics, false);
        this.drawKnob(this.highKnob.graphics, false);
    }
}
